import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  IconButton,
  Paper,
  Snackbar,
  TextField,
  Tooltip,
} from '@mui/material';
import { MaterialReactTable, useMaterialReactTable } from 'material-react-table';
import { MRT_Localization_EN } from 'material-react-table/locales/en';
import DownloadIcon from '@mui/icons-material/Download';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import FilterListIcon from '@mui/icons-material/FilterList';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import Papa from 'papaparse';

const safeExportValue = (value) => {
  if (value === null || value === undefined) return '';
  if (Array.isArray(value)) return value.join(', ');
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
};

const safeTsvCell = (value) => {
  const normalized = safeExportValue(value);
  if (!normalized) return '';
  return normalized.replace(/\t/g, ' ').replace(/\n/g, ' ');
};

const copyToClipboard = async (text) => {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }
  const textArea = document.createElement('textarea');
  textArea.value = text;
  textArea.style.position = 'fixed';
  textArea.style.opacity = '0';
  document.body.appendChild(textArea);
  textArea.select();
  document.execCommand('copy');
  document.body.removeChild(textArea);
};

const AppSheetMrtTable = ({
  rows,
  columns,
  rowCount,
  isLoading,
  onPaginationChange,
  pagination,
  title,
  manualPagination = true,
  manualFiltering = false,
  manualSorting = false,
  onGlobalFilterChange,
  onColumnFiltersChange,
  onSortingChange,
  toolbarActions,
}) => {
  const [globalFilter, setGlobalFilter] = useState('');
  const [columnFilters, setColumnFilters] = useState([]);
  const [columnVisibility, setColumnVisibility] = useState({});
  const [columnOrder, setColumnOrder] = useState([]);
  const [columnPinning, setColumnPinning] = useState({});
  const [rowSelection, setRowSelection] = useState({});
  const [sorting, setSorting] = useState([]);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [isFullscreen, setIsFullscreen] = useState(false);

  const data = useMemo(() => (Array.isArray(rows) ? rows : []), [rows]);

  const table = useMaterialReactTable({
    columns,
    data,
    localization: MRT_Localization_EN,
    getRowId: (row) => String(row?.__rowId ?? row?.id ?? row?.Id ?? row?.id ?? Math.random()),
    enableRowSelection: true,
    enableRowVirtualization: true,
    enableStickyHeader: true,
    enableColumnResizing: true,
    enableColumnOrdering: true,
    enableColumnPinning: true,
    enableGlobalFilter: true,
    enableColumnFilters: true,
    enableSorting: true,
    manualPagination,
    manualFiltering,
    manualSorting,
    onPaginationChange,
    rowCount: Number.isFinite(rowCount) ? rowCount : data.length,
    state: {
      globalFilter,
      columnFilters,
      columnVisibility,
      columnOrder,
      columnPinning,
      rowSelection,
      pagination,
      isFullScreen: isFullscreen,
      isLoading: Boolean(isLoading),
      sorting,
    },
    onGlobalFilterChange: (value) => {
      setGlobalFilter(value);
      if (typeof onGlobalFilterChange === 'function') onGlobalFilterChange(value);
    },
    onColumnFiltersChange: (value) => {
      setColumnFilters(value);
      if (typeof onColumnFiltersChange === 'function') onColumnFiltersChange(value);
    },
    onSortingChange: (value) => {
      setSorting(value);
      if (typeof onSortingChange === 'function') onSortingChange(value);
    },
    onColumnVisibilityChange: setColumnVisibility,
    onColumnOrderChange: setColumnOrder,
    onColumnPinningChange: setColumnPinning,
    onRowSelectionChange: setRowSelection,
    initialState: {
      density: 'compact',
      showGlobalFilter: false,
      showColumnFilters: false,
    },
    renderTopToolbarCustomActions: () => null,
  });

  const selectedRows = useMemo(() => {
    const selectedIds = new Set(Object.keys(rowSelection || {}));
    return data.filter((row) => selectedIds.has(String(row?.__rowId ?? row?.id ?? row?.Id)));
  }, [data, rowSelection]);

  const exportableColumns = useMemo(
    () =>
      (table.getAllLeafColumns?.() || []).filter((col) => {
        const id = col?.id;
        if (!id) return false;
        if (id === 'mrt-row-select') return false;
        if (!col.getIsVisible?.()) return false;
        const meta = col.columnDef?.meta || {};
        if (meta.disableExport) return false;
        return Boolean(col.columnDef?.header);
      }),
    [table]
  );

  const handleCopySelected = async (format) => {
    if (!selectedRows.length) return;
    if (format === 'json') {
      await copyToClipboard(JSON.stringify(selectedRows, null, 2));
      setSnackbar({ open: true, message: `Copied ${selectedRows.length} row(s) as JSON.`, severity: 'success' });
      return;
    }
    const headers = exportableColumns.map((c) => c.columnDef.header).join('\t');
    const rowsText = selectedRows
      .map((row) =>
        exportableColumns
          .map((col) => {
            const key = col.columnDef?.accessorKey;
            if (key) return safeTsvCell(row[key]);
            if (typeof col.columnDef?.accessorFn === 'function') return safeTsvCell(col.columnDef.accessorFn(row));
            return safeTsvCell(row[col.id]);
          })
          .join('\t')
      )
      .join('\n');
    await copyToClipboard(`${headers}\n${rowsText}`);
    setSnackbar({ open: true, message: `Copied ${selectedRows.length} row(s) as TSV.`, severity: 'success' });
  };

  const exportCsvFromRows = async ({ exportRows, fileName }) => {
    if (!exportableColumns.length) {
      setSnackbar({ open: true, message: 'No visible columns to export.', severity: 'info' });
      return;
    }
    const columnsToExport = exportableColumns.map((c) => c.id);
    const sanitized = exportRows.map((row) => {
      const out = {};
      exportableColumns.forEach((col) => {
        const key = col.columnDef?.accessorKey;
        let value = '';
        if (key) value = row[key];
        else if (typeof col.columnDef?.accessorFn === 'function') value = col.columnDef.accessorFn(row);
        else value = row[col.id];
        out[col.id] = safeExportValue(value);
      });
      return out;
    });
    const csv = Papa.unparse(sanitized, { quotes: true, columns: columnsToExport });
    try {
      const { saveCsvFile } = await import('../utils/fileSystemAccess');
      const result = await saveCsvFile(csv, fileName);
      if (result.success && !result.cancelled) {
        setSnackbar({ open: true, message: `Exported ${exportRows.length} row(s) to CSV.`, severity: 'success' });
        return;
      }
      if (result.cancelled) return;
    } catch (_err) {
      // noop
    }
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    setSnackbar({ open: true, message: `Exported ${exportRows.length} row(s) to CSV.`, severity: 'success' });
  };

  const handleExportVisibleCsv = async () => {
    const exportRows = table.getFilteredRowModel?.().rows?.map((r) => r.original) || [];
    if (!exportRows.length) {
      setSnackbar({ open: true, message: 'Nothing to export.', severity: 'info' });
      return;
    }
    const fileName = `${title || 'appsheet'}_export_${new Date().toISOString().split('T')[0]}.csv`;
    await exportCsvFromRows({ exportRows, fileName });
  };

  const handleExportSelectedCsv = async () => {
    if (!selectedRows.length) {
      setSnackbar({ open: true, message: 'No rows chosen for export.', severity: 'info' });
      return;
    }
    const fileName = `${title || 'appsheet'}_export_${new Date().toISOString().split('T')[0]}.csv`;
    await exportCsvFromRows({ exportRows: selectedRows, fileName });
  };

  const handleResetFilters = () => {
    setGlobalFilter('');
    setColumnFilters([]);
    try {
      table.resetColumnFilters?.();
      table.resetGlobalFilter?.();
    } catch (_err) {
      // noop
    }
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Paper
        variant="outlined"
        sx={{
          mb: 1,
          p: 1.25,
          borderRadius: 3,
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: 1,
        }}
      >
        <Chip label={`Rows: ${Number.isFinite(rowCount) ? rowCount : data.length}`} color="primary" variant="outlined" size="small" />
        <TextField
          size="small"
          placeholder="Global search…"
          value={globalFilter}
          onChange={(e) => table.setGlobalFilter?.(e.target.value)}
          sx={{ minWidth: 240 }}
        />
        <Button size="small" variant="outlined" startIcon={<FilterListIcon />} onClick={handleResetFilters}>
          Reset
        </Button>
        <Button
          size="small"
          variant="outlined"
          startIcon={<ContentCopyIcon />}
          onClick={() => void handleCopySelected('tsv')}
          disabled={!selectedRows.length}
        >
          Copy Selected TSV
        </Button>
        <Button
          size="small"
          variant="outlined"
          startIcon={<ContentCopyIcon />}
          onClick={() => void handleCopySelected('json')}
          disabled={!selectedRows.length}
        >
          Copy Selected JSON
        </Button>
        <Tooltip title="Export selected rows (or all filtered rows)">
          <span>
            <Button size="small" variant="outlined" startIcon={<DownloadIcon />} onClick={() => void handleExportVisibleCsv()}>
              Export Visible CSV
            </Button>
          </span>
        </Tooltip>
        <Tooltip title="Export only selected rows to CSV">
          <span>
            <Button size="small" variant="outlined" startIcon={<DownloadIcon />} onClick={() => void handleExportSelectedCsv()} disabled={!selectedRows.length}>
              Export Selected CSV
            </Button>
          </span>
        </Tooltip>
        <Tooltip title={isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}>
          <IconButton size="small" onClick={() => setIsFullscreen((prev) => !prev)} aria-label="Toggle fullscreen">
            {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
          </IconButton>
        </Tooltip>
        {toolbarActions}
      </Paper>

      <MaterialReactTable table={table} />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3200}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))} severity={snackbar.severity} variant="filled" sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AppSheetMrtTable;
